import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { type ComposerThreadDraftState, useComposerDraftStore } from "../../composerDraftStore";
import { type PromptStashEntry, promptStashStore } from "../../promptStashStore";

type SavePrompt = (entry: PromptStashEntry) => { readonly ok: boolean };

export function stashOlderDestinationThenMove(
  source: ScopedThreadRef,
  destination: ScopedThreadRef,
  savePrompt: SavePrompt = (entry) => promptStashStore.getState().save(entry),
): boolean {
  const store = useComposerDraftStore.getState();
  const drafts = readStagedHandoffDrafts(source, destination);
  if (!drafts) return false;
  const { destinationDraft, sourceSubmission, destinationSubmission } = drafts;
  if (!currentContentMatchesSubmission(destinationDraft, destinationSubmission)) return false;
  if (!compatibleExecutionRuntime(sourceSubmission, destinationSubmission)) return false;
  if (!sourceIsNewer(sourceSubmission.startedAt, destinationSubmission.startedAt)) return false;
  if (!canPersistInStash(destinationDraft)) return false;
  const saved = savePrompt({
    id: `handoff:${scopedThreadKey(destination)}:${destinationSubmission.commandId}`,
    createdAt: destinationSubmission.startedAt,
    prompt: destinationDraft.prompt,
    attachments: [...destinationDraft.persistedAttachments],
    terminalContexts: [...destinationDraft.terminalContexts],
  });
  if (!saved.ok) return false;
  store.clearStagedComposerSubmission(destination);
  store.clearComposerContent(destination);
  return store.moveComposerDraft(source, destination);
}

function compatibleExecutionRuntime(
  source: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
  destination: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
): boolean {
  const sourceRuntimeId = source.executionIntent?.runtimeId;
  const destinationRuntimeId = destination.executionIntent?.runtimeId;
  return !sourceRuntimeId || !destinationRuntimeId || sourceRuntimeId === destinationRuntimeId;
}

function readStagedHandoffDrafts(source: ScopedThreadRef, destination: ScopedThreadRef) {
  const store = useComposerDraftStore.getState();
  const sourceDraft = store.getComposerDraft(source);
  const destinationDraft = store.getComposerDraft(destination);
  const sourceSubmission = sourceDraft?.stagedSubmission;
  const destinationSubmission = destinationDraft?.stagedSubmission;
  if (!sourceDraft || !destinationDraft || !sourceSubmission || !destinationSubmission) return null;
  return { destinationDraft, sourceSubmission, destinationSubmission };
}

function sourceIsNewer(sourceStartedAtValue: string, destinationStartedAtValue: string): boolean {
  const sourceStartedAt = Date.parse(sourceStartedAtValue);
  const destinationStartedAt = Date.parse(destinationStartedAtValue);
  return (
    Number.isFinite(sourceStartedAt) &&
    Number.isFinite(destinationStartedAt) &&
    destinationStartedAt < sourceStartedAt
  );
}

function currentContentMatchesSubmission(
  draft: ComposerThreadDraftState,
  submission: NonNullable<ComposerThreadDraftState["stagedSubmission"]>,
): boolean {
  return (
    draft.prompt === submission.prompt &&
    sameImageMetadata(draft.images, submission.attachments) &&
    JSON.stringify(draft.persistedAttachments) === JSON.stringify(submission.attachments) &&
    JSON.stringify(draft.terminalContexts.map(persistTerminalContext)) ===
      JSON.stringify(submission.terminalContexts.map(persistTerminalContext))
  );
}

function sameImageMetadata(
  current: ComposerThreadDraftState["images"],
  staged: NonNullable<ComposerThreadDraftState["stagedSubmission"]>["attachments"],
): boolean {
  return (
    current.length === staged.length &&
    current.every((image, index) => {
      const attachment = staged[index];
      return (
        attachment !== undefined &&
        image.id === attachment.id &&
        image.name === attachment.name &&
        image.mimeType === attachment.mimeType &&
        image.sizeBytes === attachment.sizeBytes
      );
    })
  );
}

function persistTerminalContext(context: ComposerThreadDraftState["terminalContexts"][number]) {
  return {
    id: context.id,
    threadId: context.threadId,
    createdAt: context.createdAt,
    terminalId: context.terminalId,
    terminalLabel: context.terminalLabel,
    lineStart: context.lineStart,
    lineEnd: context.lineEnd,
  };
}

function canPersistInStash(draft: ComposerThreadDraftState): boolean {
  if (draft.lastGeneratedPullRequestPrompt !== undefined) return false;
  if (draft.nonPersistedImageIds.length > 0) return false;
  const persistedIds = new Set(draft.persistedAttachments.map(({ id }) => id));
  return (
    draft.images.every(({ id }) => persistedIds.has(id)) &&
    (draft.stagedSubmission?.attachments.every(({ id }) => persistedIds.has(id)) ?? true)
  );
}
